"use client";

import { c as _c } from "react/compiler-runtime";
import type { Post } from "@/payload-types";
import { useLabels } from "@/lib/labels/LabelsProvider";
import type { TierConfig } from "../config/leagues";
import type { TierData } from "../data/getNationsLeagueData";
import { FeaturedNews } from "./FeaturedNews";
import { StandingsSidebar } from "./StandingsSidebar";
import { MatchesSection } from "./MatchesSection";
import { NewsGrid } from "./NewsGrid";
interface TierBodyProps {
  tier: TierConfig;
  data: TierData;
  news: Post[];
}

/**
 * Active tier's page body. Section order mirrors `FootballCompetitionHomeClassicLayout`:
 * news + standings sidebar → matches → bottom news grid.
 *
 * News is tournament-wide (merged across all four tiers); the standings
 * sidebar is tier-scoped so switching tabs swaps the tables.
 */
export function TierBody(t0) {
  const $ = _c(32);
  const {
    data,
    news
  } = t0;
  const labels = useLabels();
  let t1;
  if ($[0] !== news) {
    t1 = news.slice(0, 6);
    $[0] = news;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const featured = t1;
  let t2;
  if ($[2] !== news) {
    t2 = news.slice(6);
    $[2] = news;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const bottom = t2;
  let t3;
  if ($[4] !== data.currentMatchday || $[5] !== data.totalMatchdays || $[6] !== labels.nationsLeague.matchdayOf) {
    t3 = labels.nationsLeague.matchdayOf.replace("{current}", String(data.currentMatchday)).replace("{total}", String(data.totalMatchdays));
    $[4] = data.currentMatchday;
    $[5] = data.totalMatchdays;
    $[6] = labels.nationsLeague.matchdayOf;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const matchdayLine = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== labels.nationsLeague.groupStage || $[10] !== matchdayLine) {
    t5 = <span className="inline-flex items-center gap-1.5 rounded-sm bg-muted px-2 py-1 font-semibold uppercase tracking-wider">{t4}{labels.nationsLeague.groupStage} · {matchdayLine}</span>;
    $[9] = labels.nationsLeague.groupStage;
    $[10] = matchdayLine;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== labels.nationsLeague.knockoutComingSoon) {
    t6 = <span className="italic">{labels.nationsLeague.knockoutComingSoon}</span>;
    $[12] = labels.nationsLeague.knockoutComingSoon;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <div className="flex items-center gap-2 text-xs text-muted-foreground">{t5}{t6}</div>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== data.standings) {
    t8 = <StandingsSidebar groups={data.standings} />;
    $[17] = data.standings;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== featured || $[20] !== t8) {
    t9 = <FeaturedNews articles={featured} sidebar={t8} />;
    $[19] = featured;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== data.matches) {
    t10 = <MatchesSection matches={data.matches} />;
    $[22] = data.matches;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== bottom || $[25] !== labels.nationsLeague.moreFromTournament) {
    t11 = bottom.length > 0 && <NewsGrid articles={bottom} title={labels.nationsLeague.moreFromTournament} />;
    $[24] = bottom;
    $[25] = labels.nationsLeague.moreFromTournament;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t7 || $[30] !== t9) {
    t12 = <main className="max-w-350 mx-auto px-4 py-6 space-y-10">{t7}{t9}{t10}{t11}</main>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t7;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
}